import { faBuildingFlag, faCircleHalfStroke, faFileSignature } from '@fortawesome/pro-solid-svg-icons';
import {
    activeDataTableFilters,
    type VxDataTableColumn,
    type VxDataTableFilter,
    type VxDataTableFilterField,
    type VxDataTableFilterSelectable,
    type VxDataTableFilterValue,
    type VxDataTableGroupByOption,
    type VxDataTableSection,
    type VxDataTableSort,
} from '@voxie/frontend-components';
import {
    GROUP_STATUS_ACTIVE,
    GROUP_STATUS_CLOSED,
    GROUP_STATUS_DRAFT,
    GROUP_STATUS_SUSPENDED,
    type GroupStatus,
} from '~/constants/groupStatuses';
import type {
    KioskTeamGroup,
    KioskTeamGroupBilling,
    KioskTeamGroupFilterKey,
    KioskTeamGroupFilters,
    KioskTeamGroupGroupBy,
    KioskTeamGroupHosting,
    KioskTeamGroupHostingFilterValue,
    KioskTeamGroupNumberState,
    KioskTeamGroupNumberType,
    KioskTeamGroupOwnerInvite,
    KioskTeamGroupPaymentMethod,
    KioskTeamGroupSection,
    KioskTeamGroupSectionsParams,
    KioskTeamGroupSetupRequiredValue,
    KioskTeamGroupSignup,
    KioskTeamGroupSortField,
    KioskTeamGroupTenDlcCampaignStatus,
    KioskTeamGroupTenDlcCampaignStatusFilterValue,
    KioskTeamGroupTerms,
    KioskTeamGroupTermsDue,
    KioskTeamGroupVendorFilterValue,
    KioskTeamGroupVoice,
    KioskTeamGroupsCountParams,
    KioskTeamGroupsParams,
} from '~/services/adminTeamGroupsClient.types';
import { formatPhoneNumber } from '~/components/filters';
import {
    campaignStatusLabels,
    useCaseLabels,
    vendorLabels,
    verifiedLabel,
} from '~/components/kiosk/kiosk-ten-dlc/tenDlcLabels';
import { formatNumberType, formatVendor } from '~/constants/phoneNumbers';
import dayjs from '~/utils/dayjs';
import { prepareCsv } from '~/utils/files';
import { formatNumber } from '~/utils/helpers';
import { stringifyQueryString } from '~/utils/string';

const KIOSK_TEAM_GROUPS_PER_PAGE = 50;
const KIOSK_TEAM_GROUPS_SECTION_PER_PAGE = 25;
const KIOSK_TEAM_GROUPS_EXPORT_PER_PAGE = 250;
const KIOSK_TEAM_GROUPS_SEARCH_MAX_LENGTH = 80;
export const KIOSK_TEAM_GROUPS_MAX_FILTER_VALUES = 10;
const COLUMNS_STORAGE_KEY_PREFIX = 'voxie:kiosk:team-groups:v3';
const DENSITY_STORAGE_KEY_PREFIX = 'voxie:kiosk:team-groups:v1';
const CALENDAR_DATE_FORMAT = 'YYYY-MM-DD';

export type KioskTeamGroupsSort = VxDataTableSort<KioskTeamGroup>;
export type KioskTeamGroupsFilter = VxDataTableFilter<KioskTeamGroup>;
export type KioskTeamGroupsSection = VxDataTableSection<KioskTeamGroup, KioskTeamGroupGroupBy>;

type KioskTeamGroupColumnKey =
    | 'name'
    | 'status'
    | 'owner'
    | 'signup'
    | 'phone_number'
    | 'terms'
    | 'billing'
    | 'users_count'
    | 'org_unit_id'
    | 'group_id'
    | 'number_type'
    | 'vendor'
    | 'hosting'
    | 'ten_dlc_campaign_status'
    | 'voice'
    | 'go_live_date'
    | 'collections_count'
    | 'created_at';

type KioskTeamGroupColumn = VxDataTableColumn<KioskTeamGroup> & { key: KioskTeamGroupColumnKey };

export const kioskTeamGroupColumns: KioskTeamGroupColumn[] = [
    { key: 'name', label: 'Group', width: 224, minWidth: 224, sortable: true, hideable: false },
    { key: 'status', label: 'Status', width: 124, minWidth: 124, sortable: true },
    { key: 'owner', label: 'Owner', width: 150, minWidth: 124, sortable: true },
    { key: 'signup', label: 'Signup', width: 120, minWidth: 110, sortable: true },
    { key: 'phone_number', label: 'Number', width: 180, minWidth: 176, sortable: true },
    { key: 'terms', label: 'T&Cs', width: 120, minWidth: 120, sortable: true },
    { key: 'billing', label: 'Billing', width: 130, minWidth: 124, sortable: true },
    { key: 'users_count', label: 'Users', width: 70, align: 'end', sortable: true },
    { key: 'org_unit_id', label: 'Organizational Unit', width: 120, sortable: true },
    { key: 'group_id', label: 'External ID', width: 200, sortable: true },
    { key: 'number_type', label: 'Number type', width: 120, sortable: true },
    { key: 'vendor', label: 'Vendor', width: 110, sortable: true },
    { key: 'hosting', label: 'Hosting', width: 120, sortable: true },
    {
        key: 'ten_dlc_campaign_status',
        label: '10DLC campaign',
        width: 240,
        minWidth: 130,
        sortable: true,
    },
    { key: 'voice', label: 'Voice', width: 120, sortable: true },
    { key: 'go_live_date', label: 'Go-live date', width: 140, sortable: true },
    { key: 'collections_count', label: 'Collections', width: 120, align: 'end', sortable: true },
    { key: 'created_at', label: 'Created', width: 140, minWidth: 120, sortable: true },
];

export const kioskTeamGroupGroupByOptions: (VxDataTableGroupByOption<KioskTeamGroup> & {
    key: KioskTeamGroupGroupBy;
})[] = [
    { key: 'status', label: 'Status', icon: faCircleHalfStroke },
    { key: 'org_unit_id', label: 'Organizational Unit', icon: faBuildingFlag },
    { key: 'terms', label: 'T&Cs', icon: faFileSignature },
];

const columnKeys: string[] = kioskTeamGroupColumns.map((column) => column.key);

const isColumnKey = (key: string): key is KioskTeamGroupColumnKey => columnKeys.includes(key);

const defaultVisibleColumnKeys: string[] = kioskTeamGroupColumns
    .filter((column) => column.visible !== false)
    .map((column) => column.key);

const lockedColumnKeys: KioskTeamGroupColumnKey[] = kioskTeamGroupColumns
    .filter((column) => column.hideable === false)
    .map((column) => column.key);

export const isColumnVisible = (visibleColumns: string[] | null, key: keyof KioskTeamGroup): boolean =>
    (visibleColumns ?? defaultVisibleColumnKeys).includes(key);

export const visibleColumnKeys = (visibleColumns: string[] | null): KioskTeamGroupColumnKey[] => {
    const keys = [...new Set(visibleColumns ?? defaultVisibleColumnKeys)].filter(isColumnKey);

    return [...keys, ...lockedColumnKeys.filter((key) => !keys.includes(key))];
};

export const columnsStorageKey = (teamId: number): string => `${COLUMNS_STORAGE_KEY_PREFIX}:${teamId}:columns`;

export const densityStorageKey = (teamId: number): string => `${DENSITY_STORAGE_KEY_PREFIX}:${teamId}:density`;

export const defaultSort = { key: 'name', direction: 'asc' } as const satisfies NonNullable<
    VxDataTableSort<KioskTeamGroup>
>;

interface FilterOption<TValue extends string> extends VxDataTableFilterValue {
    value: TValue;
    label: string;
}

const filterOptionsFrom = <TValue extends string>(labels: Record<TValue, string>): FilterOption<TValue>[] =>
    (Object.keys(labels) as TValue[]).map((value) => ({ value, label: labels[value] }));

const NO_ORG_UNIT = 'none';
const NO_NUMBER = 'none';
const NO_NUMBER_LABEL = 'No number';

const groupStatusLabels: Record<GroupStatus, string> = {
    [GROUP_STATUS_DRAFT]: 'Draft',
    [GROUP_STATUS_ACTIVE]: 'Active',
    [GROUP_STATUS_SUSPENDED]: 'Suspended',
    [GROUP_STATUS_CLOSED]: 'Closed',
};

const statusOptions = filterOptionsFrom(groupStatusLabels);

const groupStatuses: readonly string[] = statusOptions.map((option) => option.value);

export const isGroupStatus = (value: unknown): value is GroupStatus =>
    typeof value === 'string' && groupStatuses.includes(value);

const setupRequiredOptions: FilterOption<KioskTeamGroupSetupRequiredValue>[] = [
    { value: 'needs_setup', label: 'Needs setup' },
    { value: 'complete', label: 'Complete' },
];

export const noOrgUnitOption: FilterOption<typeof NO_ORG_UNIT> = { value: NO_ORG_UNIT, label: 'No OU' };

export const termsLabels: Record<KioskTeamGroupTerms, string> = {
    accepted: 'Accepted',
    not_accepted: 'Not accepted',
    no_terms: 'No terms',
};

const termsOptions = filterOptionsFrom(termsLabels);

const termsValues: readonly string[] = termsOptions.map((option) => option.value);

export const isTerms = (value: unknown): value is KioskTeamGroupTerms =>
    typeof value === 'string' && termsValues.includes(value);

const termsDueLabels: Record<Exclude<KioskTeamGroupTermsDue, ''>, string> = {
    upcoming: 'Upcoming',
    overdue: 'Overdue',
};

export const termsDueLabel = (termsDue: KioskTeamGroupTermsDue): string =>
    termsDue === '' ? '' : termsDueLabels[termsDue];

export const billingLabels: Record<Exclude<KioskTeamGroupBilling, ''>, string> = {
    complete: 'Complete',
    incomplete: 'Incomplete',
    corporate: 'Corporate',
    org_unit: 'OU',
};

const billingOptions = filterOptionsFrom(billingLabels);

const billingLabel = (billing: KioskTeamGroupBilling): string => (billing === '' ? '' : billingLabels[billing]);

export const paymentMethodLabel = (paymentMethod: KioskTeamGroupPaymentMethod, paymentTermsDays: number): string => {
    if (paymentMethod === 'card') {
        return 'Card on file';
    }

    return paymentMethod === 'invoice' ? `Invoice, Net ${paymentTermsDays}` : '';
};

const billingPaymentMethodLabel = (group: KioskTeamGroup): string => {
    if (group.billing !== 'complete' && group.billing !== 'incomplete') {
        return '';
    }

    return group.payment_method === '' ? 'None' : paymentMethodLabel(group.payment_method, group.payment_terms_days);
};

const billingItemLabel = (billing: KioskTeamGroupBilling, done: boolean): string => {
    if (billing === 'complete' || billing === 'incomplete') {
        return done ? 'Yes' : 'No';
    }

    return '';
};

export const numberStateLabels: Record<KioskTeamGroupNumberState, string> = {
    sending: 'Sending',
    assigned_not_enabled: 'Not enabled for sending',
    corporate: 'Corporate',
    none: 'No number assigned',
};

const numberStateOptions = filterOptionsFrom(numberStateLabels);

const vendorOptions = filterOptionsFrom<KioskTeamGroupVendorFilterValue>({
    ...vendorLabels,
    [NO_NUMBER]: NO_NUMBER_LABEL,
});

const hostingLabels: Record<Exclude<KioskTeamGroupHosting, ''>, string> = {
    hosted: 'Hosted',
    not_hosted: 'Not hosted',
};

const hostingOptions = filterOptionsFrom<KioskTeamGroupHostingFilterValue>({
    ...hostingLabels,
    [NO_NUMBER]: NO_NUMBER_LABEL,
});

const tenDlcCampaignStatusLabels: Record<Exclude<KioskTeamGroupTenDlcCampaignStatus, ''>, string> = {
    active: campaignStatusLabels.active,
    pending: campaignStatusLabels.pending,
    unassigned: 'Not assigned',
    attach_failed: 'Attach failed',
    failed: campaignStatusLabels.failed,
    expired: campaignStatusLabels.expired,
    not_required: 'Not required',
};

export const tenDlcCampaignStatusLabel = (status: KioskTeamGroupTenDlcCampaignStatus): string =>
    status === '' ? '' : tenDlcCampaignStatusLabels[status];

const tenDlcCampaignStatusOptions = filterOptionsFrom<KioskTeamGroupTenDlcCampaignStatusFilterValue>({
    ...tenDlcCampaignStatusLabels,
    [NO_NUMBER]: NO_NUMBER_LABEL,
});

const voiceLabels: Record<Exclude<KioskTeamGroupVoice, ''>, string> = {
    forward: 'Forward',
    shift_to_text: 'Shift to Text',
    none: 'None',
};

const ownerInviteLabels: Record<Exclude<KioskTeamGroupOwnerInvite, 'none'>, string> = {
    pending: 'Invite pending',
    expired: 'Invite expired',
};

export const ownerInviteLabel = (ownerInvite: KioskTeamGroupOwnerInvite): string =>
    ownerInvite === 'none' ? '' : ownerInviteLabels[ownerInvite];

export const signupLabels: Record<Exclude<KioskTeamGroupSignup, ''>, string> = {
    not_started: 'Not started',
    invite_expired: 'Invite expired',
    invited: 'Invited',
    inactive: 'Inactive',
    signed: 'Signed',
};

const signupOptions = filterOptionsFrom(signupLabels);

const signupLabel = (signup: KioskTeamGroupSignup): string => (signup === '' ? '' : signupLabels[signup]);

export const kioskTeamGroupFilterFields = (
    orgUnitSelectable?: VxDataTableFilterSelectable
): VxDataTableFilterField<KioskTeamGroup>[] => [
    { key: 'status', label: 'Status', values: statusOptions },
    { key: 'signup', label: 'Signup', values: signupOptions },
    { key: 'org_unit_id', label: 'Organizational Unit', values: [noOrgUnitOption], selectable: orgUnitSelectable },
    { key: 'terms', label: 'T&Cs', values: termsOptions },
    { key: 'billing', label: 'Billing', values: billingOptions },
    { key: 'number_state', label: 'Number', values: numberStateOptions },
    { key: 'vendor', label: 'Vendor', values: vendorOptions },
    { key: 'hosting', label: 'Hosting', values: hostingOptions },
    { key: 'ten_dlc_campaign_status', label: '10DLC campaign', values: tenDlcCampaignStatusOptions },
    { key: 'setup_required', label: 'Setup', values: setupRequiredOptions },
];

export const phoneNumberLabel = (phoneNumber: string): string =>
    phoneNumber === '' ? '' : formatPhoneNumber(phoneNumber);

const numberTypeLabel = (numberType: KioskTeamGroupNumberType): string =>
    numberType === '' ? '' : formatNumberType(numberType);

const vendorLabel = (vendor: string): string => (vendor === '' ? '' : formatVendor(vendor));

const hostingLabel = (hosting: KioskTeamGroupHosting): string => (hosting === '' ? '' : hostingLabels[hosting]);

const voiceLabel = (voice: KioskTeamGroupVoice): string => (voice === '' ? '' : voiceLabels[voice]);

const goLiveDateLabel = (goLiveDate: string): string => {
    const date = dayjs(goLiveDate, CALENDAR_DATE_FORMAT, true);

    return date.isValid() ? date.format('MMM Do, YYYY') : '';
};

const knownUseCaseLabels: Partial<Record<string, string>> = useCaseLabels;

export const useCaseLabel = (useCase: string): string => knownUseCaseLabels[useCase] ?? useCase;

export const brandVerifiedLabel = (verified: boolean | null): string =>
    verified === null ? '' : verifiedLabel(verified);

const plainCellTexts: Partial<Record<KioskTeamGroupColumnKey, (group: KioskTeamGroup) => string>> = {
    group_id: (group) => group.group_id,
    number_type: (group) => numberTypeLabel(group.number_type),
    vendor: (group) => vendorLabel(group.vendor),
    hosting: (group) => hostingLabel(group.hosting),
    voice: (group) => voiceLabel(group.voice),
    go_live_date: (group) => goLiveDateLabel(group.go_live_date),
    collections_count: (group) => formatNumber(group.collections_count),
};

export const plainCellText = (group: KioskTeamGroup, key: string): string =>
    (isColumnKey(key) ? plainCellTexts[key]?.(group) : undefined) ?? '';

const filterKeys: string[] = kioskTeamGroupFilterFields().map((field) => field.key);

const isFilterKey = (key: string): key is KioskTeamGroupFilterKey => filterKeys.includes(key);

export const toFilterParams = (filters: KioskTeamGroupsFilter[]): KioskTeamGroupFilters => {
    const params: KioskTeamGroupFilters = {};

    activeDataTableFilters(filters).forEach((filter) => {
        if (isFilterKey(filter.key)) {
            params[filter.key] = { operator: filter.operator, values: filter.values };
        }
    });

    return params;
};

export const filtersAllowStatus = (filters: KioskTeamGroupsFilter[], status: GroupStatus): boolean => {
    const statusFilter = toFilterParams(filters).status;

    if (statusFilter === undefined) {
        return true;
    }

    return statusFilter.values.includes(status) === (statusFilter.operator === 'in');
};

export const exceedsFilterValueLimit = (filters: KioskTeamGroupsFilter[]): boolean =>
    filters.some((filter) => filter.values.length > KIOSK_TEAM_GROUPS_MAX_FILTER_VALUES);

export const toSearchQuery = (search: string): string =>
    Array.from(search.trim()).slice(0, KIOSK_TEAM_GROUPS_SEARCH_MAX_LENGTH).join('').trimEnd();

const toSortField = (columnKey: string): string => (columnKey === 'org_unit_id' ? 'org_unit_name' : columnKey);

const sortFields: string[] = kioskTeamGroupColumns
    .filter((column) => column.sortable)
    .map((column) => toSortField(column.key));

const isSortField = (key: string): key is KioskTeamGroupSortField => sortFields.includes(key);

type SortParams = Required<Pick<KioskTeamGroupsParams, 'sort_field' | 'sort_dir'>>;

const defaultSortParams: SortParams = { sort_field: defaultSort.key, sort_dir: defaultSort.direction };

export const toSortParams = (sort: KioskTeamGroupsSort): SortParams => {
    if (sort === null) {
        return defaultSortParams;
    }

    const sortField = toSortField(sort.key);

    if (!isSortField(sortField)) {
        return defaultSortParams;
    }

    return { sort_field: sortField, sort_dir: sort.direction };
};

export const toGroupsCountParams = (query: string, filters: KioskTeamGroupsFilter[]): KioskTeamGroupsCountParams => {
    const filterParams = toFilterParams(filters);

    return {
        ...(query === '' ? {} : { query }),
        ...(Object.keys(filterParams).length === 0 ? {} : { filters: filterParams }),
    };
};

export const toGroupsParams = (
    query: string,
    sort: KioskTeamGroupsSort,
    filters: KioskTeamGroupsFilter[]
): KioskTeamGroupsParams => ({
    ...toGroupsCountParams(query, filters),
    ...toSortParams(sort),
    per_page: KIOSK_TEAM_GROUPS_PER_PAGE,
});

export const toGroupSectionsParams = (
    query: string,
    sort: KioskTeamGroupsSort,
    filters: KioskTeamGroupsFilter[],
    groupBy: KioskTeamGroupGroupBy
): KioskTeamGroupSectionsParams => ({
    ...toGroupsCountParams(query, filters),
    ...toSortParams(sort),
    per_page: KIOSK_TEAM_GROUPS_SECTION_PER_PAGE,
    group_by: groupBy,
});

export const toSectionPageParams = (
    params: KioskTeamGroupSectionsParams,
    section: string,
    cursor: string | null
): KioskTeamGroupsParams => ({
    ...params,
    section,
    cursor,
});

export type KioskTeamGroupsExportRows = 'all' | 'visible';

export type KioskTeamGroupsExportColumns = 'all' | 'visible';

export const toExportParams = (
    rows: KioskTeamGroupsExportRows,
    query: string,
    sort: KioskTeamGroupsSort,
    filters: KioskTeamGroupsFilter[]
): KioskTeamGroupsParams => ({
    ...(rows === 'visible' ? toGroupsCountParams(query, filters) : {}),
    ...toSortParams(sort),
    per_page: KIOSK_TEAM_GROUPS_EXPORT_PER_PAGE,
});

export const exportColumnKeys = (
    columns: KioskTeamGroupsExportColumns,
    visibleColumns: string[] | null
): KioskTeamGroupColumnKey[] =>
    columns === 'all' ? kioskTeamGroupColumns.map((column) => column.key) : visibleColumnKeys(visibleColumns);

interface ExportField {
    header: string;
    value: (group: KioskTeamGroup) => string | number;
}

const exportFieldsByColumn: Record<KioskTeamGroupColumnKey, ExportField[]> = {
    name: [{ header: 'Group', value: (group) => (group.setup_required ? 'Setup required' : group.name) }],
    status: [{ header: 'Status', value: (group) => groupStatusLabels[group.status] }],
    owner: [
        { header: 'Owner', value: (group) => group.owner },
        { header: 'Owner email', value: (group) => group.owner_email },
        { header: 'Owner invite', value: (group) => ownerInviteLabel(group.owner_invite) },
    ],
    signup: [
        { header: 'Signup', value: (group) => signupLabel(group.signup) },
        { header: 'Last signed in (UTC)', value: (group) => group.owner_last_login_at },
    ],
    phone_number: [
        { header: 'Number', value: (group) => phoneNumberLabel(group.phone_number) },
        { header: 'Number state', value: (group) => numberStateLabels[group.number_state] },
        { header: 'Numbers on group', value: (group) => group.phone_numbers_count },
    ],
    terms: [
        { header: 'T&Cs', value: (group) => termsLabels[group.terms] },
        { header: 'T&Cs due', value: (group) => termsDueLabel(group.terms_due) },
    ],
    billing: [
        { header: 'Billing', value: (group) => billingLabel(group.billing) },
        { header: 'Rates', value: (group) => billingItemLabel(group.billing, group.has_rates) },
        { header: 'Billing address', value: (group) => billingItemLabel(group.billing, group.has_billing_address) },
        { header: 'Payment method', value: (group) => billingPaymentMethodLabel(group) },
    ],
    users_count: [{ header: 'Users', value: (group) => group.users_count }],
    org_unit_id: [{ header: 'Organizational Unit', value: (group) => group.org_unit_name }],
    group_id: [{ header: 'External ID', value: (group) => group.group_id }],
    number_type: [{ header: 'Number type', value: (group) => numberTypeLabel(group.number_type) }],
    vendor: [{ header: 'Vendor', value: (group) => vendorLabel(group.vendor) }],
    hosting: [{ header: 'Hosting', value: (group) => hostingLabel(group.hosting) }],
    ten_dlc_campaign_status: [
        { header: '10DLC campaign', value: (group) => group.ten_dlc_campaign_label },
        { header: '10DLC status', value: (group) => tenDlcCampaignStatusLabel(group.ten_dlc_campaign_status) },
        { header: 'Brand', value: (group) => brandVerifiedLabel(group.ten_dlc_campaign_brand_verified) },
        { header: 'Vetting score', value: (group) => group.ten_dlc_campaign_brand_vetting_score ?? '' },
        { header: 'Use case', value: (group) => useCaseLabel(group.ten_dlc_campaign_use_case) },
        { header: 'AT&T class', value: (group) => group.ten_dlc_campaign_att_class },
    ],
    voice: [{ header: 'Voice', value: (group) => voiceLabel(group.voice) }],
    go_live_date: [{ header: 'Go-live date', value: (group) => group.go_live_date }],
    collections_count: [{ header: 'Collections', value: (group) => group.collections_count }],
    created_at: [{ header: 'Created (UTC)', value: (group) => group.created_at }],
};

const withExternalId = (columns: KioskTeamGroupColumnKey[]): KioskTeamGroupColumnKey[] =>
    columns.includes('group_id')
        ? columns
        : columns.flatMap((column): KioskTeamGroupColumnKey[] => (column === 'name' ? ['name', 'group_id'] : [column]));

export const toExportCsv = (groups: KioskTeamGroup[], columns: KioskTeamGroupColumnKey[]): string => {
    const fields = withExternalId(columns).flatMap((column) => exportFieldsByColumn[column]);

    return prepareCsv(
        fields.map((field) => field.header),
        groups.map((group) => fields.map((field) => field.value(group)))
    );
};

export const exportFileName = (teamId: number): string => `team-${teamId}-groups.csv`;

const hasUnloadedRows = (section: KioskTeamGroupSection): boolean =>
    section.data === null ? section.total > 0 : section.next_cursor !== null;

const toSectionValue = (
    groupBy: KioskTeamGroupGroupBy,
    section: KioskTeamGroupSection
): KioskTeamGroupsSection['value'] => {
    if (groupBy === 'status') {
        return isGroupStatus(section.value) ? section.value : null;
    }

    if (groupBy === 'terms') {
        return isTerms(section.key) ? section.key : null;
    }

    return section.key === NO_ORG_UNIT ? null : Number(section.key);
};

export const toDataTableSection =
    (groupBy: KioskTeamGroupGroupBy) =>
    (section: KioskTeamGroupSection): KioskTeamGroupsSection => ({
        key: section.key,
        value: toSectionValue(groupBy, section),
        rows: section.data ?? [],
        total: section.total,
        hasMore: hasUnloadedRows(section),
        loading: false,
    });

export const sectionCursorsFrom = (sections: KioskTeamGroupSection[]): Record<string, string | null> =>
    Object.fromEntries(sections.map((section) => [section.key, section.next_cursor]));

export const sectionNamesFrom = (sections: KioskTeamGroupSection[]): Record<string, string | null> =>
    Object.fromEntries(sections.map((section) => [section.key, section.value]));

export const unloadedSectionKeys = (sections: KioskTeamGroupSection[]): string[] =>
    sections.filter((section) => section.data === null).map((section) => section.key);

export const expandedSectionKeys = (collapsed: string[], previouslyCollapsed: string[]): string[] =>
    previouslyCollapsed.filter((key) => !collapsed.includes(key));

export const awaitsRows = (section: KioskTeamGroupsSection): boolean =>
    section.rows.length === 0 && section.hasMore === true && section.loading !== true;

export const withGroupStatus = (groups: KioskTeamGroup[], groupId: string, status: GroupStatus): KioskTeamGroup[] =>
    groups.map((group) => (group.group_id === groupId ? { ...group, status } : group));

export const withSectionGroupStatus = (
    sections: KioskTeamGroupsSection[],
    groupId: string,
    status: GroupStatus
): KioskTeamGroupsSection[] =>
    sections.map((section) => ({ ...section, rows: withGroupStatus(section.rows, groupId, status) }));

const containsGroup = (section: KioskTeamGroupsSection, groupId: string): boolean =>
    section.rows.some((group) => group.group_id === groupId);

export const sectionOfGroup = (
    sections: KioskTeamGroupsSection[],
    groupId: string
): KioskTeamGroupsSection | undefined => sections.find((section) => containsGroup(section, groupId));

const withoutGroup = (section: KioskTeamGroupsSection, groupId: string): KioskTeamGroupsSection => ({
    ...section,
    rows: section.rows.filter((group) => group.group_id !== groupId),
    total: section.total - 1,
});

export const withoutSectionGroup = (sections: KioskTeamGroupsSection[], groupId: string): KioskTeamGroupsSection[] =>
    sections
        .map((section) => (containsGroup(section, groupId) ? withoutGroup(section, groupId) : section))
        .filter((section) => section.total > 0);

export const isNarrowed = (params: KioskTeamGroupsCountParams): boolean =>
    params.query !== undefined || params.filters !== undefined;

export const nextCursorFrom = (nextPageUrl: string | null): string | null =>
    nextPageUrl === null ? null : new URLSearchParams(nextPageUrl.split('?')[1]).get('cursor');

const SHORT_GROUP_ID_LENGTH = 6;

export const shortGroupId = (groupId: string): string =>
    groupId.length > SHORT_GROUP_ID_LENGTH ? `${groupId.slice(0, SHORT_GROUP_ID_LENGTH)}…` : groupId;

export const newGroupHref = (teamId: number): string =>
    `/kiosk/groups/new?${stringifyQueryString({ team_id: teamId })}`;

export const groupImportHref = (teamId: number): string => `/kiosk/teams/${teamId}/groups/imports/new`;

export const orgUnitHref = (teamId: number, orgUnitId: number): string =>
    `/kiosk/teams/${teamId}/org-units/${orgUnitId}`;

export const groupHref = (teamId: number, group: KioskTeamGroup): string =>
    group.setup_required
        ? `/kiosk/groups/new?${stringifyQueryString({ team_id: teamId, setup_group_id: group.group_id })}`
        : `/kiosk/teams/${teamId}/groups/${group.group_id}`;
